import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface OfflineDB extends DBSchema {
  'sync-queue': {
    key: string;
    value: {
      id: string;
      userId: string;
      actionType: string;
      tableName: string;
      recordId?: string;
      payload: any;
      createdAt: string;
      status: 'pending' | 'synced' | 'failed';
      retryCount: number;
    };
    indexes: { 'by-status': string; 'by-created': string };
  };
  'offline-cache': {
    key: string;
    value: {
      key: string;
      data: any;
      timestamp: number;
      expiresAt: number;
    };
  };
}

let dbInstance: IDBPDatabase<OfflineDB> | null = null;

export async function getOfflineDB() {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB<OfflineDB>('vewly-offline', 1, {
    upgrade(db) {
      // Sync queue store
      const syncQueue = db.createObjectStore('sync-queue', {
        keyPath: 'id',
      });
      syncQueue.createIndex('by-status', 'status');
      syncQueue.createIndex('by-created', 'createdAt');

      // Offline cache store
      db.createObjectStore('offline-cache', {
        keyPath: 'key',
      });
    },
  });

  return dbInstance;
}

// Queue an action for later sync
export async function queueAction(
  userId: string,
  actionType: string,
  tableName: string,
  payload: any,
  recordId?: string
) {
  const db = await getOfflineDB();
  
  const action = {
    id: crypto.randomUUID(),
    userId,
    actionType,
    tableName,
    recordId,
    payload,
    createdAt: new Date().toISOString(),
    status: 'pending' as const,
    retryCount: 0,
  };

  await db.add('sync-queue', action);
  return action.id;
}

// Get all pending actions
export async function getPendingActions() {
  const db = await getOfflineDB();
  const index = db.transaction('sync-queue').store.index('by-status');
  return index.getAll('pending');
}

// Mark action as synced
export async function markActionSynced(actionId: string) {
  const db = await getOfflineDB();
  const action = await db.get('sync-queue', actionId);
  if (action) {
    action.status = 'synced';
    await db.put('sync-queue', action);
  }
}

// Mark action as failed
export async function markActionFailed(actionId: string) {
  const db = await getOfflineDB();
  const action = await db.get('sync-queue', actionId);
  if (action) {
    action.status = 'failed';
    action.retryCount += 1;
    await db.put('sync-queue', action);
  }
}

// Cache data for offline access
export async function cacheData(key: string, data: any, ttl: number = 3600000) {
  const db = await getOfflineDB();
  
  await db.put('offline-cache', {
    key,
    data,
    timestamp: Date.now(),
    expiresAt: Date.now() + ttl,
  });
}

// Get cached data
export async function getCachedData(key: string) {
  const db = await getOfflineDB();
  const cached = await db.get('offline-cache', key);
  
  if (!cached) return null;
  
  // Check if expired
  if (cached.expiresAt < Date.now()) {
    await db.delete('offline-cache', key);
    return null;
  }
  
  return cached.data;
}

// Clear old cache entries
export async function clearExpiredCache() {
  const db = await getOfflineDB();
  const tx = db.transaction('offline-cache', 'readwrite');
  const store = tx.store;
  
  const allEntries = await store.getAll();
  const now = Date.now();
  
  for (const entry of allEntries) {
    if (entry.expiresAt < now) {
      await store.delete(entry.key);
    }
  }
  
  await tx.done;
}
